import { JSX, ReactNode, useContext, useEffect, useState } from 'react';
import { AbTestingContext } from '@src/contexts/ab-testing-context';
import { AnalyticsUserContext } from '@src/contexts/analytics-user-context';
import AbTestingClient from 'storefront-analytics/dist/ab-testing-client';
import {
  getBrowser,
  getSessionAcquisitionData,
  getSessionAttributes,
  getActivationAttributes,
  getOperatingSystem,
  getDeviceViewType,
} from 'storefront-analytics/dist/misc-utils';
import AutoTestActivation from 'storefront-analytics/dist/auto-test-activation';
import WindowService from '@src/services/window.service';
import { PAGE_TYPE } from '@src/helpers/analytics.helper';
import {
  getExperimentOverridesFromCookies,
  getExperimentOverridesFromUrl,
  saveExperimentOverridesToCookies,
} from '@src/helpers/ab-testing.helper';

/**
 * Initializes an {@link AbTestingClient} to power A/B testing on the page. Uses
 * a combination of props and information from the {@link AnalyticsUserContext} to
 * perform initialization. This should wrap an entire page to make the
 * {@link AbTestingContext} available on that page.
 */
export const AbTestingProvider = ({
  abTestingRequested,
  optimizelySdkKey,
  optimizelyDataFile,
  children,
}: {
  /** True if A/B Testing is enabled by feature toggle */
  abTestingRequested: boolean;
  /** Optimizely SDK Key (not a secret) */
  optimizelySdkKey: string;
  /**
   * Optimizely Data File (reflects current state of Optimizely experiments in
   * this environment)
   */
  optimizelyDataFile: unknown;
  children: ReactNode;
}): JSX.Element => {
  const { loaded: userContextLoaded, analyticsUserContextResponse } =
    useContext(AnalyticsUserContext);

  /** True when we've successfully initialized an AbTestingClient */
  const [initialized, setInitialized] = useState(false);
  /** Stores error received while initializing AbTestingClient */
  const [initError, setInitError] = useState(null);
  /** Stores the AbTestingClient **/
  const [abTestingClient, setAbTestingClient] = useState<AbTestingClient>(null);

  useEffect(() => {
    if (!WindowService.window.chewy) WindowService.window.chewy = {};

    if (abTestingRequested) {
      let client = abTestingClient;
      const userAgent = WindowService.window.navigator.userAgent;

      // Initialize if we haven't yet and have all prereqs loaded
      if (userContextLoaded && !initialized && !initError) {
        const {
          userContextData,
          experimentOverrides: globalExperimentOverrides,
        } = analyticsUserContextResponse;
        const { personalizationId } = userContextData;

        // Obtain experiment overrides from URL params
        const urlExperimentOverrides = getExperimentOverridesFromUrl();

        // Persist URL overrides to cookies, then obtain all the
        // resulting overrides from cookies. May include overrides
        // from previous sessions.
        saveExperimentOverridesToCookies(urlExperimentOverrides);
        const urlAndCookieOverrides = getExperimentOverridesFromCookies();

        try {
          client = new AbTestingClient(
            optimizelySdkKey,
            personalizationId,
            getSessionAttributes(
              userContextData,
              getDeviceViewType(userAgent),
              getSessionAcquisitionData(),
              getBrowser(userAgent),
              getOperatingSystem(userAgent),
              PAGE_TYPE,
            ),
            getActivationAttributes(userContextData),
            {
              // Apply user overrides (URL+cookies) on top of global ones
              ...globalExperimentOverrides,
              ...urlAndCookieOverrides,
            },
            4,
            optimizelyDataFile,
          );
          // Enable automatically activating experiments when elements with a
          // particular class .js-test-activation-trigger are in the viewport
          AutoTestActivation();
          setAbTestingClient(client);
          setInitialized(true);
        } catch (e) {
          console.error('Failed to initialize AbTestingClient.', e);
          setInitError(e);
        }
      }

      // Sync the client to the window so other libraries (e.g. Header/Footer)
      // can use it
      if (client) {
        WindowService.window.chewy.optimizelyClient = client;
      }
    }

    return () => {
      WindowService.window.chewy.optimizelyClient = undefined;
    };
  }, [
    abTestingClient,
    abTestingRequested,
    initError,
    initialized,
    optimizelyDataFile,
    optimizelySdkKey,
    analyticsUserContextResponse,
    userContextLoaded,
  ]);

  return (
    <AbTestingContext.Provider
      value={{
        abTestingEnabled: abTestingRequested,
        abTestingInitialized: initialized,
        initializationError: initError,
        abTestingClient,
      }}
    >
      {children}
    </AbTestingContext.Provider>
  );
};
